import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import {
  EnvelopeIcon,
  MapPinIcon,
  PhoneArrowUpRightIcon,
} from "@heroicons/react/20/solid";
import { CallCenterAPI } from "../../utils/callcenterAPI";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export const Contactus = () => {
  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    tel: "",
    message: "",
    website: "", // honeypot
  });

  const [status, setStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onChange = (e) =>
    setValues({ ...values, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("");

    // honeypot
    if (values.website) {
      setStatus("Submitted");
      return;
    }

    if (!SITE_KEY) {
      setStatus("Missing reCAPTCHA site key");
      return;
    }

    setIsSubmitting(true);
    try {
      // Important: grecaptcha.ready then execute

      await new Promise((resolve) => window.grecaptcha.ready(resolve));
      const token = await window.grecaptcha.execute(SITE_KEY, {
        action: "contact_us",
      });

      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        tel: values.tel,
        message: values.message,
        // website: values.website || "",
        recaptchaToken: token,
      };

      const res = await CallCenterAPI().sendEmailV2(payload);

      if (res?.success) {
        toast.success("Message sent successfully!");
      }

      setStatus(
        res?.message ||
          "Thank you for contacting us! we will get back to you soon."
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Server error"
      );
      setStatus(err?.response?.data?.message || err?.message || "Server error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="bg-pagePattern bg-center">
        <div className="flex mx-auto max-w-screen-lg text-center justify-center items-center mb-9 h-32">
          <span className="text-7xl text-textWhite font-medium">
            Contact Us
          </span>
        </div>
      </div>

      <div className="relative isolate bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2">
          {/* Left column unchanged */}
          <div className="relative px-6 pb-20 pt-24 sm:pt-32 lg:static lg:px-8 lg:py-48">
            <div className="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
              <h2 className="text-3xl font-bold tracking-tight text-primary">
                Get in touch
              </h2>
              <p className="mt-6 text-lg leading-8 text-gray-600">
                Please feel free to contact us, If you would like more
                information about our products.
              </p>

              <dl className="mt-10 space-y-4 text-base leading-7 text-gray-600">
                <div className="flex gap-x-4">
                  <dt className="flex-none">
                    <span className="sr-only">Address</span>
                    <MapPinIcon className="text-primary h-6 md:h-8" />
                  </dt>
                  <dd>Al Rahmaniya, Sharjah, U.A.E. P.O. Box: 79979</dd>
                </div>
                <div className="flex gap-x-4">
                  <dt className="flex-none">
                    <span className="sr-only">Telephone</span>
                    <PhoneArrowUpRightIcon className="text-primary h-6" />
                  </dt>
                  <dd>600 55 6666</dd>
                </div>
                <div className="flex gap-x-4">
                  <dt className="flex-none">
                    <span className="sr-only">Email</span>
                    <EnvelopeIcon
                      className="h-7 w-6 text-primary"
                      aria-hidden="true"
                    />
                  </dt>
                  <dd>
                    <a
                      className="hover:text-cool-gray-600"
                      href="mailto:help@zulal.ae"
                    >
                      help@zulal.ae
                    </a>
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Form */}
          <form
            method="POST"
            id="demo-form"
            onSubmit={handleSubmit}
            className="px-6 pb-24 pt-20 sm:pb-32 lg:px-8 lg:py-2"
          >
            <div className="mx-auto max-w-xl lg:mr-0 lg:max-w-lg">
              <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                {/* controlled inputs so reset works */}
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    First name
                  </label>
                  <div className="mt-2.5">
                    <input
                      type="text"
                      name="firstName"
                      value={values.firstName}
                      onChange={onChange}
                      required
                      id="firstName"
                      autoComplete="given-name"
                      placeholder="First Name"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
                       placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    Last name
                  </label>
                  <div className="mt-2.5">
                    <input
                      type="text"
                      name="lastName"
                      value={values.lastName}
                      onChange={onChange}
                      required
                      id="lastName"
                      autoComplete="family-name"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
                       placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    Email
                  </label>
                  <div className="mt-2.5">
                    <input
                      type="email"
                      name="email"
                      value={values.email}
                      onChange={onChange}
                      required
                      id="email"
                      autoComplete="email"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
                       placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="tel"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    Phone number
                  </label>
                  <div className="mt-2.5">
                    <input
                      type="tel"
                      name="tel"
                      value={values.tel}
                      onChange={onChange}
                      required
                      id="tel"
                      autoComplete="tel"
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
                       placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    Message
                  </label>
                  <div className="mt-2.5">
                    <textarea
                      name="message"
                      value={values.message}
                      onChange={onChange}
                      id="message"
                      required
                      rows={4}
                      className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
                       placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                {/* honeypot hidden */}

                <input
                  type="text"
                  name="website"
                  value={values.website}
                  onChange={onChange}
                  tabIndex={-1}
                  autoComplete="off"
                  style={{ display: "none" }}
                />
              </div>

              <div className="mt-8 flex flex-col justify-end">
                <div>
                  {/* <button
                    data-sitekey="6LcpuMUsAAAAAP2UfpkjxpU7Lm7dn0u8JxOh1Skr"
                    data-callback={handleSubmit}
                    data-action="submit"
                    type="submit"
                    disabled={isSubmitting || !altchaPayload}
                    className="rounded-md bg-secondary px-3.5 py-2.5 text-center text-sm font-semibold text-textWhite shadow-sm hover:bg-secondaryHover
                      disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Sending..." : "Send message"}
                  </button> */}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="rounded-md bg-secondary px-3.5 py-2.5 text-center text-sm font-semibold text-textWhite shadow-sm hover:bg-secondaryHover
                      disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Sending..." : "Send message"}
                  </button>
                </div>

                {status && (
                  <div className="mt-4">
                    <span className="text-base font-bold text-red-500">
                      {status}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
